
<template>
  <div class="container mx-auto flex flex-col items-center justify-center my-8 bg-white py-8 rounded-lg">
    <div class="w-full max-w-2xl overflow-x-auto text-grayDark">
      <form @submit.prevent="submitAddTalent" class="overflow-y-auto px-6">
        <div class="space-y-8 mb-6">
          <div class="flex items-center gap-2">
            <label class="w-[112px] block text-[14px] font-medium text-gray-700">Choose a Project</label>
            <CustomSelect :options="projects" v-model="selectedProject" labelKey="project_name" valueKey="project_id"
              placeholder="Select a project" @change="onProjectChange" />
            <div v-if="projectLoading" class="text-blue-500 text-sm mt-1">Loading projects...</div>
            <div v-if="projectError" class="text-red-500 text-sm mt-1">{{ projectError }}</div>
          </div>

          <div class="flex items-center gap-2">
            <label class="w-[112px] block text-[14px] font-medium text-gray-700">Select a Team</label>
            <CustomSelect :options="teams" v-model="selectedTeam" labelKey="team_name" valueKey="team_id"
              placeholder="Select a team" :disabled="!selectedProject || teamsLoading" />
            <div v-if="teamsLoading" class="text-blue-500 text-sm mt-1">Loading teams...</div>
            <div v-if="teamError" class="text-red-500 text-sm mt-1">{{ teamError }}</div>
          </div>

          <div class="flex items-center gap-2">
            <label class="w-[112px] block text-[14px] font-medium text-gray-700">Select Talent</label>
            <CustomSelect :options="formattedTalents" v-model="selectedTalents" labelKey="displayName" valueKey="talent_id"
              placeholder="Select Talent" multiple :disabled="!selectedTeam || talentsLoading" />
            <div v-if="talentsLoading" class="text-blue-500 text-sm mt-1">Loading talents...</div>
            <div v-if="talentError" class="text-red-500 text-sm mt-1">{{ talentError }}</div>
          </div>
        </div>

        <div class="flex justify-end mt-10 text-[14px]">
          <button type="button" class="mr-4 py-2 px-4 bg-primaryLight border border-grayLight text-grayDark rounded"
            @click="resetForm">Cancel</button>
          <button type="submit" class="flex items-center gap-2 py-2 px-6 bg-primaryDarkBg h-[42px] text-white rounded"
            :disabled="submitDisabled">
            <img src="@/assets/dashboard/add.svg" alt="User icon" class="h-4">
            Invite Member
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import apiClient from '@/axios';
import CustomSelect from './CustomSelect.vue';

export default {
  name: 'AddTalentToTeam',
  components: {
    CustomSelect
  },
  props: {
    projects: {
      type: Array,
      default: () => []
    },
    preSelectedProjectId: {
      type: [String, Number],
      default: null
    },
    preSelectedTeamId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      selectedProject: null,
      selectedTeam: null,
      selectedTalents: [],
      teams: [],
      talents: [],
      projectLoading: false,
      teamsLoading: false,
      talentsLoading: false,
      projectError: null,
      teamError: null,
      talentError: null,
      localProjects: []
    }
  },
  computed: {
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isManager() {
      return this.$store.getters.isManager;
    },
    submitDisabled() {
      return !this.selectedProject || !this.selectedTeam || this.selectedTalents.length === 0 ||
        this.projectLoading || this.teamsLoading || this.talentsLoading;
    },
    formattedTalents() {
      return this.talents.map(talent => {
        const email = talent.talent_email || '';
        const firstName = talent.talent_first_name || email;
        const lastName = talent.talent_last_name || '';
        const displayName = `${firstName} ${lastName}`;

        return {
          ...talent,
          displayName,
          email
        };
      });
    },
    displayProjects() {
      return this.projects.length > 0 ? this.projects : this.localProjects;
    }
  },
  methods: {
    async onProjectChange() {
      if (this.selectedProject) {
        this.selectedTeam = null;
        this.selectedTalents = [];
        await this.fetchTeams(this.selectedProject);
        await this.fetchProjectMembers(this.selectedProject);
      } else {
        this.teams = [];
        this.talents = [];
      }
    },
    async fetchTeams(projectId) {
      try {
        this.teamsLoading = true;
        this.teamError = null;
        const response = await apiClient.get(`/web/teams/project/${projectId}`);

        if (response.data && response.data.data) {
          this.teams = response.data.data;

          if (this.preSelectedTeamId && this.teams.some(team => team.team_id == this.preSelectedTeamId)) {
            this.selectedTeam = this.preSelectedTeamId;
          }
        } else {
          this.teamError = 'Received invalid data format from server';
          this.teams = [];
        }
      } catch (error) {
        this.teamError = error.response?.data?.message || 'Failed to load teams';
        this.teams = [];
      } finally {
        this.teamsLoading = false;
      }
    },
    async fetchProjectMembers(projectId) {
      if (!projectId) {
        return;
      }

      try {
        this.talentsLoading = true;
        this.talentError = null;

        const response = await apiClient.get(`/web/projects/${projectId}/project_members/`);

        if (response.data && response.data.data) {
          this.talents = response.data.data;
        } else {
          this.talentError = 'Received invalid data format from server';
          this.talents = [];
        }
      } catch (error) {
        this.talentError = error.response?.data?.message || 'Failed to load project members';
        this.talents = [];
      } finally {
        this.talentsLoading = false;
      }
    },

    async submitAddTalent() {
      if (!this.selectedProject || !this.selectedTeam || this.selectedTalents.length === 0) {
        this.$emit('error', "Please fill all required fields");
        return;
      }

      try {
        const teamId = this.selectedTeam;

        const results = {
          succeeded: [],
          failed: [],
          errorMessages: []
        };

        for (const talentId of this.selectedTalents) {
          try {
            const talent = this.talents.find(t => t.talent_id === talentId);

            if (!talent) {
              results.failed.push(talentId);
              continue;
            }

            const projectMemberId = talent.project_member_id;

            if (!projectMemberId) {
              results.failed.push(talentId);
              continue;
            }

            const talentData = {
              project_member_id: projectMemberId
            };

            await apiClient.post(`/web/teams/${teamId}/members`, talentData);

            results.succeeded.push({
              talentId,
              name: talent.user?.first_name + ' ' + talent.user?.last_name
            });

            this.$emit('success', "Member added successfully");

          } catch (error) {
            results.failed.push(talentId);

            const errorMessage = error.response?.data?.message || error.message || 'Unknown error';

            if (!results.errorMessages.includes(errorMessage)) {
              results.errorMessages.push(errorMessage);
            }
          }
        }

        if (results.succeeded.length > 0) {
          this.$emit('talents-added', {
            teamId: this.selectedTeam,
            projectId: this.selectedProject,
            talent_ids: results.succeeded.map(t => t.talentId),
            talent_names: results.succeeded.map(t => t.name)
          });

          this.resetForm();
        }

        if (results.failed.length > 0) {
          const errorMessage = results.errorMessages.length > 0
            ? results.errorMessages.join('; ')
            : 'Failed to add some team members';

          this.$emit('error', errorMessage);

          if (results.succeeded.length === 0) {
            return;
          }
        }

      } catch (error) {
        const errorMessage = error.response?.data?.message ||
          error.message ||
          'Failed to add talents to team';

        this.$emit('error', errorMessage);
      }
    },

    resetForm() {
      this.selectedProject = null;
      this.selectedTeam = null;
      this.selectedTalents = [];
    }
  }
}
</script>
